/**
 * PrintableRoleMaterials — generates A4-formatted role packs for all participants.
 *
 * Route: /print/:simRunId (all roles) or /print/:simRunId/:roleId (single role)
 * Uses browser native print (window.print()) + CSS @media print.
 *
 * SPEC: MODULES/M6_HUMAN_INTERFACE/SPEC_PRINTABLE_MATERIALS.md
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { CountryEmblem } from '@/components/CountryEmblem'

/* ── Types ──────────────────────────────────────────────────────────────── */

interface Role {
  id: string; character_name: string; title: string; country_code: string
  positions: string[]; public_bio: string; confidential_brief: string | null
  objectives: string[]; powers: string[]
  age: number; parliament_seat: number
  intelligence_pool: number; sabotage_cards: number; assassination_cards: number
  cyber_cards: number; disinfo_cards: number; election_meddling_cards: number
  protest_stim_cards: number; fatherland_appeal: number
}

interface Country {
  id: string; sim_name: string; color_ui: string | null
  gdp: number; stability: number; treasury: number; inflation: number; debt_burden: number
  nuclear_level: number; ai_level: number; regime_type: string
  country_brief: string | null
  mil_ground: number; mil_naval: number; mil_tactical_air: number
  mil_strategic_missiles: number; mil_air_defense: number
  prod_cost_ground: number; prod_cost_naval: number; prod_cost_tactical: number
  prod_cap_ground: number; prod_cap_naval: number; prod_cap_tactical: number
  mobilization_pool: number; tax_rate: number; trade_balance: number
  sector_resources: number; sector_industry: number; sector_services: number; sector_technology: number
}

interface Artefact {
  id: string; title: string; artefact_type: string; classification: string
  from_entity: string | null; date_label: string | null; content: string | null
  role_id: string; visible_to: string[] | null
}

interface Relationship {
  from_country_code: string; to_country_code: string; relationship: string
}

interface OrgMembership {
  org_id: string; country_code: string; role_id: string; role_in_org: string; has_veto: boolean
}

interface Organization {
  id: string; sim_name: string
}

interface RoleAction {
  role_id: string; action_id: string
}

interface PersonalRel {
  role_id: string; other_role: string; type: string; notes: string
}

interface KeyEvent {
  name: string; type: string; round: number; subtype?: string
  country_code?: string; organization?: string; nominations_round?: number
  participants?: { role_id: string; country_code: string; character_name: string }[]
  note?: string
}

/* ── Action metadata ────────────────────────────────────────────────────── */

const ACTION_INFO: Record<string, { label: string; timing: string; category: string }> = {
  public_statement: { label: 'Public Statement', timing: 'during round', category: 'communication' },
  invite_to_meet: { label: 'Set Meetings', timing: 'during round', category: 'communication' },
  call_org_meeting: { label: 'Call Org Meeting', timing: 'during round (if chair)', category: 'communication' },
  set_budget: { label: 'Set Budget', timing: 'once per round', category: 'economic' },
  set_tariffs: { label: 'Set Tariffs', timing: 'once per round', category: 'economic' },
  set_sanctions: { label: 'Set Sanctions', timing: 'once per round', category: 'economic' },
  set_opec: { label: 'Set Cartel Production', timing: 'once per round (OPEC)', category: 'economic' },
  propose_transaction: { label: 'Propose Transaction', timing: 'during round', category: 'diplomatic' },
  propose_agreement: { label: 'Propose Agreement', timing: 'during round', category: 'diplomatic' },
  basing_rights: { label: 'Basing Rights', timing: 'during round', category: 'diplomatic' },
  declare_war: { label: 'Declare War', timing: 'during round', category: 'diplomatic' },
  attack: { label: 'Attack', timing: 'during round', category: 'military' },
  naval_blockade: { label: 'Naval Blockade', timing: 'during round', category: 'military' },
  move_units: { label: 'Move Units', timing: 'between rounds only', category: 'military' },
  martial_law: { label: 'Martial Law', timing: 'during round', category: 'military' },
  nuclear_test: { label: 'Nuclear Test', timing: 'during round', category: 'military' },
  nuclear_launch_initiate: { label: 'Nuclear Launch', timing: 'during round', category: 'military' },
  reassign_types: { label: 'Reassign Powers', timing: 'during round', category: 'political' },
  arrest: { label: 'Arrest', timing: 'during round', category: 'political' },
  change_leader: { label: 'Change Leader', timing: 'during round', category: 'political' },
  self_nominate: { label: 'Self-Nominate', timing: 'one round before election', category: 'political' },
  cast_vote: { label: 'Cast Vote', timing: 'during election', category: 'political' },
  intelligence: { label: 'Intelligence Report', timing: 'during round', category: 'covert' },
  covert_operation: { label: 'Covert Operation', timing: 'during round', category: 'covert' },
  assassination: { label: 'Assassination', timing: 'during round', category: 'covert' },
}

const POS_LABEL: Record<string, string> = {
  head_of_state: 'Head of State', military: 'Military Chief', military_chief: 'Military Chief',
  economy: 'Economy Officer', economy_officer: 'Economy Officer',
  diplomat: 'Diplomat', security: 'Security', opposition: 'Opposition',
}

/* ── Component ──────────────────────────────────────────────────────────── */

export function PrintableRoleMaterials() {
  const { simRunId, roleId } = useParams<{ simRunId: string; roleId?: string }>()
  const [loading, setLoading] = useState(true)
  const [roles, setRoles] = useState<Role[]>([])
  const [countries, setCountries] = useState<Country[]>([])
  const [artefacts, setArtefacts] = useState<Artefact[]>([])
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [orgMemberships, setOrgMemberships] = useState<OrgMembership[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [roleActions, setRoleActions] = useState<RoleAction[]>([])
  const [personalRels, setPersonalRels] = useState<PersonalRel[]>([])
  const [keyEvents, setKeyEvents] = useState<KeyEvent[]>([])
  const [sanctions, setSanctions] = useState<{imposer_country_code:string;target_country_code:string;level:number}[]>([])
  const [tariffs, setTariffs] = useState<{imposer_country_code:string;target_country_code:string;level:number}[]>([])

  useEffect(() => {
    if (!simRunId) return
    const load = async () => {
      const [rolesRes, countriesRes, artefactsRes, relsRes, orgMemRes, orgsRes, raRes, srRes, depsRes] = await Promise.all([
        supabase.from('roles').select('*').eq('sim_run_id', simRunId).eq('status', 'active').order('country_code').order('character_name'),
        supabase.from('countries').select('*').eq('sim_run_id', simRunId).neq('id', '_note').order('gdp', { ascending: false }),
        supabase.from('artefacts').select('*').eq('sim_run_id', simRunId),
        supabase.from('relationships').select('*').eq('sim_run_id', simRunId),
        supabase.from('org_memberships').select('*').eq('sim_run_id', simRunId),
        supabase.from('organizations').select('id,sim_name').eq('sim_run_id', simRunId),
        supabase.from('role_actions').select('role_id,action_id').eq('sim_run_id', simRunId),
        supabase.from('sim_runs').select('key_events').eq('id', simRunId).limit(1),
        supabase.from('deployments').select('country_code,unit_type').eq('sim_run_id', simRunId).neq('unit_status', 'destroyed'),
      ])

      // Inject live military counts from deployments
      const cs = (countriesRes.data || []) as Country[]
      const deps = depsRes.data || []
      const mc: Record<string,{mil_ground:number;mil_naval:number;mil_tactical_air:number;mil_strategic_missiles:number;mil_air_defense:number}> = {}
      for(const d of deps){
        if(!mc[d.country_code]) mc[d.country_code]={mil_ground:0,mil_naval:0,mil_tactical_air:0,mil_strategic_missiles:0,mil_air_defense:0}
        if(d.unit_type==='ground') mc[d.country_code].mil_ground++
        else if(d.unit_type==='naval') mc[d.country_code].mil_naval++
        else if(d.unit_type==='tactical_air') mc[d.country_code].mil_tactical_air++
        else if(d.unit_type==='strategic_missile') mc[d.country_code].mil_strategic_missiles++
        else if(d.unit_type==='air_defense') mc[d.country_code].mil_air_defense++
      }
      for(const c of cs){ const m=mc[c.id]; if(m) Object.assign(c,m); else { c.mil_ground=0;c.mil_naval=0;c.mil_tactical_air=0;c.mil_strategic_missiles=0;c.mil_air_defense=0 } }

      setRoles((rolesRes.data || []) as Role[])
      setCountries(cs)
      setArtefacts((artefactsRes.data || []) as Artefact[])
      setRelationships((relsRes.data || []) as Relationship[])
      setOrgMemberships((orgMemRes.data || []) as OrgMembership[])
      setOrganizations((orgsRes.data || []) as Organization[])
      setRoleActions((raRes.data || []) as RoleAction[])
      setKeyEvents(((srRes.data?.[0] as Record<string, unknown>)?.key_events as KeyEvent[]) || [])

      // Load sanctions + tariffs
      const [sanctionsRes, tariffsRes] = await Promise.all([
        supabase.from('sanctions').select('imposer_country_code,target_country_code,level').eq('sim_run_id', simRunId),
        supabase.from('tariffs').select('imposer_country_code,target_country_code,level').eq('sim_run_id', simRunId),
      ])
      setSanctions((sanctionsRes.data || []) as typeof sanctions)
      setTariffs((tariffsRes.data || []) as typeof tariffs)

      // Load personal relationships from character_relationships if exists
      try {
        const { data: pRels } = await supabase.from('character_relationships').select('*').eq('sim_run_id', simRunId)
        if (pRels) setPersonalRels(pRels as PersonalRel[])
      } catch { /* table may not exist */ }

      setLoading(false)
    }
    load()
  }, [simRunId])

  const printRoles = roleId ? roles.filter(r => r.id === roleId) : roles

  if (loading) return <div style={{ padding: 40, fontFamily: 'DM Sans, sans-serif', textAlign: 'center', color: '#888' }}>Loading role materials...</div>

  const getCountry = (code: string) => countries.find(c => c.id === code)
  const getOrgName = (orgId: string) => organizations.find(o => o.id === orgId)?.sim_name || orgId
  const getCountryName = (code: string) => countries.find(c => c.id === code)?.sim_name || code

  const wars = new Set<string>()
  relationships.filter(r => r.relationship === 'at_war').forEach(r => {
    const pair = [r.from_country_code, r.to_country_code].sort().join(' — ')
    wars.add(pair)
  })

  return (
    <div>
      {/* Sidebar controls (hidden in print) */}
      <div className="no-print" style={{ position: 'fixed', top: 20, right: 20, zIndex: 100, background: '#1a1a2e', border: '1px solid #333', borderRadius: 12, padding: 20, width: 200, fontFamily: 'DM Sans, sans-serif', color: '#ccc' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#fff' }}>Print Roles</h3>
        <p style={{ fontSize: 11, margin: '0 0 12px', color: '#888' }}>{printRoles.length} roles · ~{printRoles.length * 6} pages</p>
        <button onClick={() => window.print()} style={{ width: '100%', padding: '10px 0', background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          Print / PDF
        </button>
        <button onClick={() => {
          const html = document.documentElement.outerHTML
          const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url; a.download = `TTT_Role_Packs_${new Date().toISOString().slice(0, 10)}.html`
          a.click(); URL.revokeObjectURL(url)
        }} style={{ width: '100%', padding: '10px 0', background: 'transparent', color: '#888', border: '1px solid #444', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
          Export HTML
        </button>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { page-break-after: always; page-break-inside: avoid; }
          body { margin: 0; padding: 0; }
        }
        .print-page {
          width: 180mm; height: 267mm; padding: 15mm 15mm 20mm 15mm; margin: 0 auto; overflow: hidden;
          font-family: 'DM Sans', sans-serif; font-size: 10pt; line-height: 1.45; color: #1a1a1a;
          position: relative; box-sizing: border-box;
        }
        @media screen { .print-page { border: 1px solid #ddd; margin-bottom: 20px; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.1); } }
        .print-page h1 { font-family: 'Playfair Display', serif; font-size: 20pt; margin: 0 0 4px; color: #111; }
        .print-page h2 { font-family: 'Playfair Display', serif; font-size: 13pt; margin: 12px 0 6px; color: #222; border-bottom: 1px solid #ccc; padding-bottom: 3px; }
        .print-page h3 { font-family: 'DM Sans', sans-serif; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin: 10px 0 4px; }
        .print-page .subtitle { font-size: 10pt; color: #666; margin-bottom: 8px; }
        .print-page .confidential { color: #c0392b; font-weight: 700; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.15em; }
        .print-page .data { font-family: 'JetBrains Mono', monospace; font-size: 9pt; }
        .print-page .kpi-row { display: flex; gap: 16px; flex-wrap: wrap; margin: 6px 0; }
        .print-page .kpi { font-family: 'JetBrains Mono', monospace; font-size: 9pt; }
        .print-page .kpi-label { font-size: 7pt; color: #888; text-transform: uppercase; }
        .print-page table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        .print-page td, .print-page th { padding: 2px 6px; text-align: left; border-bottom: 1px solid #eee; }
        .print-page th { font-size: 7pt; text-transform: uppercase; letter-spacing: 0.1em; color: #888; }
        .print-page .footer { position: absolute; bottom: 12mm; left: 15mm; right: 15mm; display: flex; justify-content: space-between; font-size: 7pt; color: #aaa; border-top: 1px solid #ddd; padding-top: 4px; }
        .print-page .obj-list { padding-left: 18px; margin: 4px 0; }
        .print-page .obj-list li { margin-bottom: 3px; }
        .print-page blockquote { border-left: 3px solid #ccc; margin: 8px 0; padding: 4px 12px; font-style: italic; color: #555; }
        .print-page.artefact-page { height: auto; min-height: 267mm; overflow: visible; }
        .artefact-content { font-family: 'DM Sans', sans-serif; }
        .artefact-content p { margin: 6px 0; }
        .artefact-content h1, .artefact-content h2, .artefact-content h3 { font-family: 'Playfair Display', serif; margin: 10px 0 4px; }
        .artefact-content h2 { font-size: 12pt; }
        .artefact-content h3 { font-size: 10pt; }
        .artefact-content ul, .artefact-content ol { padding-left: 18px; margin: 4px 0; }
        .artefact-content li { margin-bottom: 3px; }
        .artefact-content strong { font-weight: 700; }
        .artefact-content table { width: 100%; border-collapse: collapse; margin: 6px 0; }
        .artefact-content td, .artefact-content th { padding: 3px 6px; border: 1px solid #ddd; font-size: 9pt; }
      `}</style>

      {/* === RENDER ALL ROLES === */}
      {printRoles.map(role => {
        const country = getCountry(role.country_code)
        const myArtefacts = artefacts.filter(a => a.role_id === role.id || (a.visible_to && a.visible_to.includes(role.id)))
        const myActions = roleActions.filter(ra => ra.role_id === role.id).map(ra => ra.action_id)
        const myOrgs = orgMemberships.filter(om => om.role_id === role.id)
        const myPRels = personalRels.filter(pr => pr.role_id === role.id)
        const countryRoles = roles.filter(r => r.country_code === role.country_code)
        const countryRels = relationships.filter(r => r.from_country_code === role.country_code || r.to_country_code === role.country_code)

        // Determine position categories
        const positions = new Set(role.positions || [])
        const hasMilitary = positions.has('military') || positions.has('military_chief')
        const hasEconomy = positions.has('economy') || positions.has('economy_officer')
        const hasDiplomat = positions.has('diplomat')
        const hasSecurity = positions.has('security')
        const hasHoS = positions.has('head_of_state')
        const needsPositionPage = hasMilitary || hasEconomy || hasDiplomat || hasSecurity || hasHoS

        // Filter key events relevant to this role
        const myEvents = keyEvents.filter(ev => {
          if (ev.country_code === role.country_code) return true
          if (ev.organization && myOrgs.some(om => om.org_id === ev.organization)) return true
          if (ev.participants?.some(p => p.role_id === role.id)) return true
          return false
        })
        const isDirectlyInvolved = (ev: KeyEvent) => {
          if (ev.participants?.some(p => p.role_id === role.id)) return true
          if (ev.organization && myOrgs.some(om => om.org_id === ev.organization)) return true
          if (ev.type === 'election' && ev.country_code === role.country_code) return true
          return false
        }

        // Communication vs role-specific actions
        const commActions = myActions.filter(a => ACTION_INFO[a]?.category === 'communication')
        const roleSpecificActions = myActions.filter(a => ACTION_INFO[a]?.category !== 'communication')

        const Footer = ({ page }: { page: string }) => (
          <div className="footer">
            <span>Created by MetaGames Lab</span>
            <span>{role.character_name} · {country?.sim_name || role.country_code}</span>
            <span>{page}</span>
          </div>
        )

        return (
          <div key={role.id}>
            {/* ══════ COVER PAGE ══════ */}
            <div className="print-page">
              <div style={{ textAlign: 'center', paddingTop: 2, marginBottom: 6 }}>
                <img src="/logo-96.png" alt="TTT" style={{ width: 56, height: 56, marginBottom: 4, borderRadius: '50%', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
                <h1 style={{ fontSize: '19pt', marginBottom: 0, letterSpacing: '0.05em' }}>THE THUCYDIDES TRAP</h1>
              </div>

              <h2 style={{ marginTop: 8 }}>The Phenomenon</h2>
              <p style={{ fontSize: '9.5pt' }}>The Greek historian Thucydides, writing about the Peloponnesian War (431–404 BCE), observed: <em>"It was the rise of Athens and the fear that this instilled in Sparta that made war inevitable."</em> Neither side wanted war — but the structural dynamics of power transition made it catastrophic.</p>
              <p style={{ fontSize: '9.5pt' }}>Harvard professor Graham Allison revived this insight in his 2017 book <em>Destined for War</em>. His research team studied 16 cases of power transition over the past 500 years. Twelve ended in war — a 75% rate. The trap operates through fear and perception spirals, alliance entanglement, domestic pressure, shifts in relative capability, and third-party provocations. War is never inevitable — but escaping it requires extraordinary statecraft.</p>

              <h2>The World You Enter</h2>
              <p style={{ fontSize: '9.5pt' }}><strong>Columbia</strong> is the established superpower — dominant in military reach, financial systems, and alliance networks. <strong>Cathay</strong> is the rising challenger — its economy growing at pace to overtake Columbia, its navy expanding rapidly, its nuclear arsenal modernizing, and its AI technology increasingly competitive. The question of <strong>Formosa</strong> — Cathay's claim over a technologically critical island — looms as the most dangerous flashpoint in the world.</p>
              <p style={{ fontSize: '9.5pt' }}>The world is already on fire. Two wars are active: <strong>Ruthenia fights Sarmatia</strong> in Eastern Ereb, testing whether borders can be changed by force; and <strong>Columbia is at war with Persia</strong> in the Mashriq, where nuclear ambitions and oil geopolitics collide. Columbia's president has made controversial claims over <strong>Caribe</strong> and <strong>Thule</strong> — allies' sovereign territory — shaking the foundation of the alliance system from within.</p>
              <p style={{ fontSize: '9.5pt' }}><strong>Columbia's elections</strong> are not just a domestic event — they will shape the trajectory of global order. Will the next leader honor existing commitments? What kind of leadership does the world need?</p>
              <p style={{ fontSize: '9.5pt' }}>Meanwhile, every other nation recalculates. Will the <strong>Ereb Union</strong> find unity or fragment? Will <strong>Bharata</strong> emerge as a global swing power? Who will lead the Middle East — and at what cost? What form will global alliances and governance take when the old order can no longer hold? And the <strong>AI technology race</strong> — whoever reaches the frontier first may gain a decisive, permanent advantage across all domains.</p>
              <p style={{ fontSize: '9.5pt', fontStyle: 'italic', color: '#555' }}>The central question: what happens to the global order when the power that built it can no longer sustain it alone — and the power replacing it offers a different vision of how the world should work? Can you be among the 25% who find another way?</p>

              <div style={{ position: 'absolute', bottom: 64, left: '15mm', right: '15mm', padding: '8px 12px', border: '1.5px solid #3B82F6', borderRadius: 6, fontSize: '9pt' }}>
                <strong>Your Navigator:</strong> You have a personal AI advisor. Talk to Navigator before the simulation starts — ask about rules, strategy, your role, or anything else. Navigator is your guide throughout the entire experience.
              </div>

              <Footer page="Cover" />
            </div>

            {/* ══════ PAGE 1: CONFIDENTIAL BRIEFING ══════ */}
            <div className="print-page">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ flexShrink: 0 }}><CountryEmblem countryId={role.country_code} color={country?.color_ui || '#666'} size={36} /></div>
                <div>
                  <h1>{role.character_name}</h1>
                  <div className="subtitle">{role.title} · {country?.sim_name || role.country_code} · {(role.positions || []).map(p => POS_LABEL[p] || p).join(' + ')}</div>
                </div>
              </div>

              {role.confidential_brief && <>
                <h2>Confidential Briefing</h2>
                <p style={{ whiteSpace: 'pre-wrap' }}>{role.confidential_brief}</p>
              </>}

              {myPRels.length > 0 && <>
                <h2>Personal Relationships</h2>
                <table><tbody>
                  {myPRels.map((pr, i) => <tr key={i}><td style={{ fontWeight: 600, width: 120 }}>{pr.other_role}</td><td style={{ color: '#666', width: 80 }}>{pr.type}</td><td>{pr.notes}</td></tr>)}
                </tbody></table>
              </>}

              <Footer page="Page 1 — Confidential Briefing" />
            </div>

            {/* ══════ PAGE 2: OBJECTIVES & ORGANIZATIONS ══════ */}
            <div className="print-page">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ flexShrink: 0 }}><CountryEmblem countryId={role.country_code} color={country?.color_ui || '#666'} size={36} /></div>
                <div>
                  <h1>{role.character_name}</h1>
                  <div className="subtitle">{role.title} · {country?.sim_name || role.country_code} · {(role.positions || []).map(p => POS_LABEL[p] || p).join(' + ')}</div>
                </div>
              </div>

              {role.objectives?.length > 0 && <>
                <h2>Your Objectives</h2>
                <ul className="obj-list" style={{ listStyleType: 'disc' }}>{role.objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
              </>}

              {myOrgs.length > 0 && <>
                <h2>Organizations</h2>
                <table><tbody>
                  {myOrgs.map((om, i) => <tr key={i}><td style={{ fontWeight: 600 }}>{getOrgName(om.org_id)}</td><td>{om.role_in_org}</td><td>{om.has_veto ? <strong style={{ color: '#c0392b' }}>VETO</strong> : ''}</td></tr>)}
                </tbody></table>
              </>}

              <Footer page="Page 2 — Objectives & Organizations" />
            </div>

            {/* ══════ PAGE 3: ACTIONS & SCHEDULE ══════ */}
            <div className="print-page">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ flexShrink: 0 }}><CountryEmblem countryId={role.country_code} color={country?.color_ui || '#666'} size={36} /></div>
                <div>
                  <h1>{role.character_name}</h1>
                  <div className="subtitle">{role.title} · {country?.sim_name || role.country_code} · {(role.positions || []).map(p => POS_LABEL[p] || p).join(' + ')}</div>
                </div>
              </div>

              <p style={{ fontSize: '9.5pt', marginBottom: 6 }}>Communication is your main tool. Talk to anyone, negotiate, make statements, form alliances.</p>

              <h2>Your Actions</h2>
              <table>
                <thead><tr><th style={{ width: 80 }}>Type</th><th>Action</th><th style={{ width: 130 }}>When</th><th style={{ width: 30 }}>Limit</th></tr></thead>
                <tbody>
                  {(() => {
                    const allActions = [...commActions, ...roleSpecificActions]
                    const categoryOrder = ['communication', 'political', 'diplomatic', 'economic', 'military', 'covert']
                    const categoryLabels: Record<string, string> = { communication: 'General', political: 'Political', diplomatic: 'Diplomatic', economic: 'Economic', military: 'Military', covert: 'Covert' }
                    const limitMap: Record<string, number> = {}
                    if (role.intelligence_pool > 0) limitMap['intelligence'] = role.intelligence_pool
                    if (role.sabotage_cards > 0) limitMap['covert_operation'] = role.sabotage_cards
                    if (role.assassination_cards > 0) limitMap['assassination'] = role.assassination_cards
                    if (role.cyber_cards > 0) limitMap['cyber'] = role.cyber_cards
                    if (role.disinfo_cards > 0) limitMap['disinfo'] = role.disinfo_cards

                    const sorted = allActions
                      .map(a => ({ id: a, info: ACTION_INFO[a] }))
                      .filter(a => a.info)
                      .sort((a, b) => categoryOrder.indexOf(a.info.category) - categoryOrder.indexOf(b.info.category))

                    let lastCat = ''
                    return sorted.map(({ id, info }) => {
                      const showCat = info.category !== lastCat
                      lastCat = info.category
                      const limit = limitMap[id]
                      return <tr key={id}>
                        <td style={{ fontSize: '8pt', color: '#888', verticalAlign: 'top' }}>{showCat ? categoryLabels[info.category] || info.category : ''}</td>
                        <td style={{ fontWeight: 600 }}>{info.label}</td>
                        <td style={{ color: '#666', fontSize: '8pt' }}>{info.timing}</td>
                        <td className="data" style={{ textAlign: 'center' }}>{limit != null ? limit : ''}</td>
                      </tr>
                    })
                  })()}
                </tbody>
              </table>

              <h3 style={{ marginTop: 10 }}>Round Structure</h3>
              <p style={{ fontSize: '9pt', margin: '2px 0' }}><strong>Phase A:</strong> Submit actions, negotiate, act freely.</p>
              <p style={{ fontSize: '9pt', margin: '2px 0' }}><strong>Phase B:</strong> World updates (economy, politics, technology), then move units.</p>

              {myEvents.length > 0 && <>
                <h2>Key Events</h2>
                {[...new Set(myEvents.map(e => e.round))].sort().map(round => (
                  <div key={round} style={{ marginBottom: 4 }}>
                    <h3>Round {round}</h3>
                    {myEvents.filter(e => e.round === round).map((ev, i) => (
                      <div key={i} style={{ fontSize: '9pt', marginLeft: 8 }}>
                        {isDirectlyInvolved(ev) ? <strong>★ {ev.name}</strong> : ev.name}
                        {ev.nominations_round && <span style={{ color: '#666', fontSize: '8pt' }}> (nominations R{ev.nominations_round})</span>}
                        {ev.note && <span style={{ color: '#666', fontSize: '8pt' }}> — {ev.note}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </>}

              <Footer page="Page 3 — Actions & Schedule" />
            </div>

            {/* ══════ PAGE 4: YOUR COUNTRY ══════ */}
            <div className="print-page">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <CountryEmblem countryId={role.country_code} color={country?.color_ui || '#666'} size={30} />
                <h1 style={{ fontSize: '18pt' }}>{country?.sim_name || role.country_code}</h1>
              </div>

              <h2>Key Indicators</h2>
              <div className="kpi-row">
                <div><span className="kpi-label">GDP</span><br/><span className="kpi">ç{country?.gdp?.toFixed(0)}</span></div>
                <div><span className="kpi-label">Stability</span><br/><span className="kpi">{country?.stability?.toFixed(1)}/10</span></div>
                <div><span className="kpi-label">Treasury</span><br/><span className="kpi">ç{country?.treasury?.toFixed(0)}</span></div>
                <div><span className="kpi-label">Nuclear</span><br/><span className="kpi">L{country?.nuclear_level || 0}</span></div>
                <div><span className="kpi-label">AI Tech</span><br/><span className="kpi">L{country?.ai_level || 0}</span></div>
                <div><span className="kpi-label">Regime</span><br/><span className="kpi">{country?.regime_type || '—'}</span></div>
                <div><span className="kpi-label">Inflation</span><br/><span className="kpi">{country?.inflation?.toFixed(0)}%</span></div>
              </div>

              {country?.country_brief && <><h2>Country Brief</h2><p style={{ whiteSpace: 'pre-wrap' }}>{country.country_brief}</p></>}

              <Footer page="Page 4 — Your Country" />
            </div>

            {/* ══════ PAGE 5: POSITION DETAIL (conditional, right after country) ══════ */}
            {needsPositionPage && country && <div className="print-page">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <CountryEmblem countryId={role.country_code} color={country.color_ui || '#666'} size={30} />
                <h1 style={{ fontSize: '18pt' }}>{country.sim_name}</h1>
              </div>

              {hasMilitary && <>
                <h2>Military Forces</h2>
                <div className="kpi-row">
                  <div><span className="kpi-label">Ground</span><br/><span className="kpi">{country.mil_ground}</span></div>
                  <div><span className="kpi-label">Naval</span><br/><span className="kpi">{country.mil_naval}</span></div>
                  <div><span className="kpi-label">Air</span><br/><span className="kpi">{country.mil_tactical_air}</span></div>
                  <div><span className="kpi-label">Missiles</span><br/><span className="kpi">{country.mil_strategic_missiles}</span></div>
                  <div><span className="kpi-label">Air Defense</span><br/><span className="kpi">{country.mil_air_defense}</span></div>
                </div>
                <h3>Production</h3>
                <table><thead><tr><th>Type</th><th>Cost</th><th>Capacity/rd</th></tr></thead><tbody>
                  <tr><td>Ground</td><td className="data">ç{country.prod_cost_ground}</td><td className="data">{country.prod_cap_ground}</td></tr>
                  <tr><td>Naval</td><td className="data">ç{country.prod_cost_naval}</td><td className="data">{country.prod_cap_naval}</td></tr>
                  <tr><td>Air</td><td className="data">ç{country.prod_cost_tactical}</td><td className="data">{country.prod_cap_tactical}</td></tr>
                </tbody></table>
                {country.mobilization_pool > 0 && <p style={{ fontSize: '9pt' }}>Mobilization pool: {country.mobilization_pool} units</p>}
              </>}

              {hasEconomy && <>
                <h2>Economy</h2>
                <div className="kpi-row">
                  <div><span className="kpi-label">GDP</span><br/><span className="kpi">ç{country.gdp.toFixed(0)}</span></div>
                  <div><span className="kpi-label">Tax Rate</span><br/><span className="kpi">{(country.tax_rate * 100).toFixed(0)}%</span></div>
                  <div><span className="kpi-label">Inflation</span><br/><span className="kpi">{country.inflation.toFixed(0)}%</span></div>
                  <div><span className="kpi-label">Debt</span><br/><span className="kpi">{(country.debt_burden * 100).toFixed(0)}%</span></div>
                  <div><span className="kpi-label">Trade Balance</span><br/><span className="kpi">ç{country.trade_balance?.toFixed(1) || '0'}</span></div>
                </div>
                <h3>Economic Structure</h3>
                <div className="kpi-row">
                  <div><span className="kpi-label">Resources</span><br/><span className="kpi">{country.sector_resources || 0}%</span></div>
                  <div><span className="kpi-label">Industry</span><br/><span className="kpi">{country.sector_industry || 0}%</span></div>
                  <div><span className="kpi-label">Services</span><br/><span className="kpi">{country.sector_services || 0}%</span></div>
                  <div><span className="kpi-label">Technology</span><br/><span className="kpi">{country.sector_technology || 0}%</span></div>
                </div>

                {(() => {
                  const cc = role.country_code
                  const sanctionsAgainst = sanctions.filter(s => s.target_country_code === cc && s.level !== 0)
                  const sanctionsWeImpose = sanctions.filter(s => s.imposer_country_code === cc && s.level !== 0)
                  const tariffsAgainst = tariffs.filter(t => t.target_country_code === cc && t.level !== 0)
                  const tariffsWeImpose = tariffs.filter(t => t.imposer_country_code === cc && t.level !== 0)
                  const hasAny = sanctionsAgainst.length > 0 || sanctionsWeImpose.length > 0 || tariffsAgainst.length > 0 || tariffsWeImpose.length > 0
                  if (!hasAny) return null
                  const levelLabel = (l: number) => l === -1 ? 'Help avoid' : l === 1 ? 'Light' : l === 2 ? 'Medium' : l === 3 ? 'Heavy' : String(l)
                  const tariffLabel = (l: number) => l === 1 ? 'Low' : l === 2 ? 'Medium' : l === 3 ? 'High' : String(l)
                  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                    <div>
                      <h3>Sanctions Against Us</h3>
                      {sanctionsAgainst.length === 0 ? <p style={{ fontSize: '8pt', color: '#888' }}>None</p> :
                        <table><tbody>{sanctionsAgainst.map((s, i) => <tr key={i}><td>{getCountryName(s.imposer_country_code)}</td><td className="data">{levelLabel(s.level)}</td></tr>)}</tbody></table>}
                    </div>
                    <div>
                      <h3>Sanctions We Impose</h3>
                      {sanctionsWeImpose.length === 0 ? <p style={{ fontSize: '8pt', color: '#888' }}>None</p> :
                        <table><tbody>{sanctionsWeImpose.map((s, i) => <tr key={i}><td>{getCountryName(s.target_country_code)}</td><td className="data">{levelLabel(s.level)}</td></tr>)}</tbody></table>}
                    </div>
                    <div>
                      <h3>Tariffs Against Us</h3>
                      {tariffsAgainst.length === 0 ? <p style={{ fontSize: '8pt', color: '#888' }}>None</p> :
                        <table><tbody>{tariffsAgainst.map((t, i) => <tr key={i}><td>{getCountryName(t.imposer_country_code)}</td><td className="data">{tariffLabel(t.level)}</td></tr>)}</tbody></table>}
                    </div>
                    <div>
                      <h3>Tariffs We Impose</h3>
                      {tariffsWeImpose.length === 0 ? <p style={{ fontSize: '8pt', color: '#888' }}>None</p> :
                        <table><tbody>{tariffsWeImpose.map((t, i) => <tr key={i}><td>{getCountryName(t.target_country_code)}</td><td className="data">{tariffLabel(t.level)}</td></tr>)}</tbody></table>}
                    </div>
                  </div>
                })()}
              </>}

              <h2>Leadership</h2>
              <table>
                <thead><tr><th>Character</th><th>Title</th><th>Position</th></tr></thead>
                <tbody>
                  {countryRoles.map(r => <tr key={r.id} style={r.id === role.id ? { fontWeight: 700, background: '#f0f4ff' } : {}}>
                    <td>{r.character_name}{r.id === role.id ? ' (you)' : ''}</td>
                    <td>{r.title}</td>
                    <td>{(r.positions || []).map(p => POS_LABEL[p] || p).join(', ')}</td>
                  </tr>)}
                </tbody>
              </table>

              <Footer page="Page 5 — Position Detail" />
            </div>}

            {/* ══════ PAGE 6: THE WORLD ══════ */}
            <div className="print-page">
              <h1 style={{ fontSize: '18pt' }}>The World</h1>

              {(() => {
                const milIndex = (c: Country) => c.mil_ground * 1 + c.mil_naval * 3 + c.mil_tactical_air * 2 + c.mil_air_defense * 3 + c.mil_strategic_missiles * 2
                const milRanked = [...countries].sort((a, b) => milIndex(b) - milIndex(a))
                const milRank = (c: Country) => milRanked.findIndex(x => x.id === c.id) + 1
                const nukeCountries = countries.filter(c => c.nuclear_level > 0).sort((a, b) => b.nuclear_level - a.nuclear_level)
                const nukeRank = (c: Country) => c.nuclear_level > 0 ? nukeCountries.findIndex(x => x.id === c.id) + 1 : 0
                const aiCountries = countries.filter(c => c.ai_level > 0).sort((a, b) => b.ai_level - a.ai_level)
                const aiRank = (c: Country) => c.ai_level > 0 ? aiCountries.findIndex(x => x.id === c.id) + 1 : 0
                return <>
                  <h2>20 Countries</h2>
                  <table>
                    <thead><tr><th>#</th><th>Country</th><th>GDP</th><th>Stab</th><th>Military</th><th>Nuke</th><th>AI</th></tr></thead>
                    <tbody>
                      {countries.map((c, i) => {
                        const mi = milIndex(c)
                        const mr = milRank(c)
                        const nr = nukeRank(c)
                        const ar = aiRank(c)
                        return <tr key={c.id} style={c.id === role.country_code ? { fontWeight: 700, background: '#f0f4ff' } : {}}>
                          <td className="data">{i + 1}</td>
                          <td>{c.sim_name}{c.id === role.country_code ? ' ★' : ''}</td>
                          <td className="data">ç{c.gdp.toFixed(0)}</td>
                          <td className="data">{c.stability.toFixed(1)}</td>
                          <td className="data">#{mr}</td>
                          <td className="data">{nr > 0 ? `#${nr}` : '—'}</td>
                          <td className="data">{ar > 0 ? `#${ar}` : '—'}</td>
                        </tr>
                      })}
                    </tbody>
                  </table>
                </>
              })()}

              {wars.size > 0 && <>
                <h2>Wars</h2>
                {Array.from(wars).map((w, i) => <div key={i} style={{ color: '#c0392b', fontWeight: 600, fontSize: '9pt' }}>{w.split(' — ').map(c => getCountryName(c)).join(' — ')}</div>)}
              </>}

              <Footer page="Page 6 — The World" />
            </div>

            {/* Old position detail + actions pages removed — now Pages 3 + 5 above */}

            {/* ══════ ARTEFACT PAGES (conditional) ══════ */}
            {myArtefacts.map((art, idx) => (
              <div key={art.id} className="print-page artefact-page">
                <div style={{ borderBottom: '2px solid #333', paddingBottom: 8, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <div className="confidential" style={{ fontSize: '9pt', letterSpacing: '0.2em' }}>{art.classification?.toUpperCase() || 'CLASSIFIED'}</div>
                    <div style={{ fontSize: '8pt', color: '#666', fontStyle: 'italic', textTransform: 'capitalize' }}>{art.artefact_type?.replace(/_/g, ' ')}</div>
                  </div>
                  <h1 style={{ fontSize: '15pt', marginBottom: 2 }}>{art.title}</h1>
                  {(art.from_entity || art.date_label) && <div style={{ fontSize: '9pt', color: '#555' }}>
                    {art.from_entity && <span>From: <strong>{art.from_entity}</strong></span>}
                    {art.from_entity && art.date_label && <span> · </span>}
                    {art.date_label && <span>Date: {art.date_label}</span>}
                  </div>}
                </div>
                <div className="artefact-content" style={{ lineHeight: 1.55, fontSize: '9.5pt' }} dangerouslySetInnerHTML={{ __html: (art as Record<string,unknown>).content_html as string || art.content || '' }} />
                <Footer page={`Artefact ${idx + 1}`} />
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}
